import React, { useState, useContext, createContext, useEffect } from 'react';

// Create Language Context
const LanguageContext = createContext();

// Language translations
const translations = {
    'en-UK': {
        navbar: {
            adflyn_chatbot: 'Adflyn Chatbot',
            social_media: 'Social Media Management',
            calendar: 'Calendar'
        },
        profile: {
            my_profile: 'My Profile',
            settings: 'Settings',
            business_dna: 'Business DNA',
            general: 'General',
            preferences: 'Preferences',
            billing: 'Billing',
            logout: 'Logout'
        }
    },
    'en-US': {
        navbar: {
            adflyn_chatbot: 'Adflyn Chatbot',
            social_media: 'Social Media Management',
            calendar: 'Calendar'
        },
        profile: {
            my_profile: 'My Profile',
            settings: 'Settings',
            business_dna: 'Business DNA',
            general: 'General',
            preferences: 'Preferences',
            billing: 'Billing',
            logout: 'Logout'
        }
    },
    'kn': {
        navbar: {
            adflyn_chatbot: 'ಅಡ್‌ಫ್ಲಿನ್ ಚ್ಯಾಟ್‌ಬಾಟ್',
            social_media: 'ಸೋಶಿಯಲ್ ಮೀಡಿಯಾ ನಿರ್ವಹಣೆ',
            calendar: 'ಕ್ಯಾಲೆಂಡರ್'
        },
        profile: {
            my_profile: 'ನನ್ನ ಪ್ರೊಫೈಲ್',
            settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
            business_dna: 'ವ್ಯವಸಾಯ DNA',
            general: 'ಸಾಮಾನ್ಯ',
            preferences: 'ಆದ್ಯತೆಗಳು',
            billing: 'ಬಿಲ್ಲಿಂಗ್',
            logout: 'ಲಾಗ್ ಔಟ್'
        }
    },
    'ta': {
        navbar: {
            adflyn_chatbot: 'அட்‌ப்ளைன் சாட்‌பாட்',
            social_media: 'சோஷ்യல் மீடியா நிர்வாகம்',
            calendar: 'நாட்காட்சி'
        },
        profile: {
            my_profile: 'என் சுயவிவரம்',
            settings: 'அமைப்புகள்',
            business_dna: 'வணிக DNA',
            general: 'பொதுவான',
            preferences: 'விருப்பங்கள்',
            billing: 'பணம் செலுத்துதல்',
            logout: 'வெளிற்று'
        }
    },
    'hi': {
        navbar: {
            adflyn_chatbot: 'एडफ्लिन चैटबॉट',
            social_media: 'सोशल मीडिया प्रबंधन',
            calendar: 'कैलेंडर'
        },
        profile: {
            my_profile: 'मेरी प्रोफ़ाइल',
            settings: 'सेटिंग्स',
            business_dna: 'व्यावसायिक DNA',
            general: 'सामान्य',
            preferences: 'प्राथमिकताएं',
            billing: 'बिलिंग',
            logout: 'लॉग आउट'
        }
    },
    'ml': {
        navbar: {
            adflyn_chatbot: 'അഡ്‌ഫ്ലിൻ ചാറ്റ്‌ബോട്ട്',
            social_media: 'സോഷ്യൽ മീഡിയ നിർവ്വഹണം',
            calendar: 'കലണ്ടർ'
        },
        profile: {
            my_profile: 'എന്റെ പ്രൊഫൈൽ',
            settings: 'ക്രമീകരണങ്ങൾ',
            business_dna: 'ബിസിനസ് DNA',
            general: 'പൊതുവായ',
            preferences: 'മുൻഗണനകൾ',
            billing: 'ബില്ലിംഗ്',
            logout: 'ലോഗ് ഔട്ട്'
        }
    },
    'te': {
        navbar: {
            adflyn_chatbot: 'అడ్‌ఫ్లిన్ చాట్‌బాట్',
            social_media: 'సోషల్ మీడియా నిర్వహణ',
            calendar: 'క్యాలెండర్'
        },
        profile: {
            my_profile: 'నా ప్రొఫైల్',
            settings: 'సెట్టింగ్‌లు',
            business_dna: 'వ్యాపార DNA',
            general: 'సాధారణ',
            preferences: 'ప్రాధాన్యతలు',
            billing: 'బిల్లింగ్',
            logout: 'లాగ్ అవుట్'
        }
    }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        // Get language from localStorage or default to English UK
        const savedLanguage = localStorage.getItem('selectedLanguage');
        return savedLanguage || 'en-UK';
    });

    useEffect(() => {
        // Save language preference to localStorage
        localStorage.setItem('selectedLanguage', language);
        // Set HTML lang attribute for accessibility
        document.documentElement.lang = language;
    }, [language]);

    const changeLanguage = (lang) => {
        setLanguage(lang);
    };

    const t = (path) => {
        const keys = path.split('.');
        let value = translations[language];

        for (let key of keys) {
            value = value?.[key];
            if (!value) return path; // Return path if translation not found
        }

        return value || path;
    };

    return (
        <LanguageContext.Provider value={{ language, changeLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

// Hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext;
